import {InputStatus} from "@gs-ux-uitoolkit-react/input";
import React, {Fragment, useEffect, useRef, useState} from "react";
import {Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Transition} from "@headlessui/react";
import clsx from "clsx";
import {Icon, materialArrowDropDownFill} from "@gs-ux-uitoolkit-react/icon";
import {Text} from "@gs-ux-uitoolkit-react/text";

type SelectOptionItem = { label: string; value: string; disabled?: boolean; hidden?: boolean }
type SelectComboProps = {
    options: Array<SelectOptionItem>;
    selectedValue?: string;
    status?: InputStatus;
    onChange: (selected: SelectOptionItem) => void;
    onBlur?: (e: React.FocusEvent<any>) => void;
    disabled?: boolean;
    placeholder?: string;
    name?: string;
    nullable?: boolean;
    allowCustomValue?: boolean;
}

export function SelectComboInput({
                                     options,
                                     selectedValue,
                                     onChange,
                                     disabled,
                                     placeholder,
                                     onBlur,
                                     status,
                                     name,
                                     allowCustomValue
                                 }: SelectComboProps) {
    const initialOptions = allowCustomValue && selectedValue && !options.find(it => it.value === selectedValue) ? [...options, {
        label: selectedValue,
        value: selectedValue
    }] : options;
    const [query, setQuery] = useState('')
    const [selectOptions, setSelectOptions] = useState(initialOptions)

    const buttonRef = useRef<HTMLButtonElement>(null)
    const filteredData =
        query === ''
            ? selectOptions
            : selectOptions.filter((it) =>
                it.label
                    .toLowerCase()
                    .replace(/\s+/g, '')
                    .includes(query.toLowerCase().replace(/\s+/g, ''))
            )


    const showAddCustomOption = query.length > 0 && allowCustomValue
    const selectedOption = selectOptions.find(it => it.value === selectedValue);
    useEffect(() => {
        const currOptions = allowCustomValue && selectedValue && !options.find(it => it.value === selectedValue) ? [...options, {
            label: selectedValue,
            value: selectedValue
        }] : options;
        setSelectOptions(currOptions)
    }, [allowCustomValue, options, selectedValue]);

    return (
        <Combobox
            value={selectedOption || null}
            onChange={(newSelected: SelectOptionItem | null) => {
                if (!newSelected) return;
                if (showAddCustomOption && newSelected?.value === query) {
                    setSelectOptions(prev => [{label: query, value: query}, ...prev])
                }
                onChange(newSelected)
            }}
            disabled={disabled}
        >
            <div className="relative mt-1">
                <div
                    className={clsx("h-8 flex items-center relative w-full border cursor-default overflow-hidden rounded-sm bg-white text-left focus-visible:border-0", {
                        "border-gray-50": status !== "success" && status !== "error",
                        "border-red-50": status === "error",
                        "opacity-50": disabled
                    })}
                >
                    <ComboboxButton
                        className="flex items-start pr-2 bg-white w-full"
                        ref={buttonRef}
                        name={name + "-button"}
                    >
                        <ComboboxInput
                            placeholder={placeholder}
                            className="w-full py-0.5 pl-3 pr-10 text-sm leading-5 focus-visible:!outline-0 cursor-pointer"
                            displayValue={(option: SelectOptionItem) => option?.label || ""}
                            onChange={(event) => setQuery(event.target.value)}
                            onBlur={onBlur}
                            name={name}
                            id={name}
                        />

                        <div className="absolute inset-y-0 right-0 ">
                            <Icon
                                svg={materialArrowDropDownFill}
                                aria-hidden
                            />

                        </div>

                    </ComboboxButton>
                </div>
                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setQuery('')}
                >
                    <ComboboxOptions
                        className="absolute mt-0.5 max-h-60 w-full overflow-auto shadow-lg rounded-md bg-white py-1 text-base sm:text-sm z-20">
                        {showAddCustomOption && (
                            <ComboboxOption
                                className={({active, selected}) =>
                                    clsx("relative select-none py-1.5 pl-3 pr-4 cursor-pointer", {
                                        "bg-gray-20": active && !selected,
                                        "bg-blue-20": selected,
                                    })}
                                value={{label: query, value: query}}
                            >
                                <Text>
                                    Create "{query}"
                                </Text>
                            </ComboboxOption>
                        )}
                        {!allowCustomValue && filteredData.length === 0 && query !== '' ? (
                            <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                Nothing found.
                            </div>
                        ) : (
                            filteredData.map((option) => (
                                <ComboboxOption
                                    key={option.value}
                                    className={({active, selected}) =>
                                        clsx("relative select-none py-1.5 pl-3 pr-4 ", {
                                            "bg-gray-20": active && !selected,
                                            "bg-blue-20": selected,
                                            "opacity-50 cursor-not-allowed": option.disabled,
                                            "cursor-pointer": !option.disabled,
                                            "hidden": option.hidden,
                                        })}
                                    disabled={option.disabled}
                                    value={option}
                                >
                                    {({selected}) => (
                                        <Text>
                                            {option.label}
                                        </Text>

                                    )}
                                </ComboboxOption>
                            ))
                        )}
                    </ComboboxOptions>
                </Transition>
            </div>
        </Combobox>
    )
}